export interface StructuredArticleSection {
  id: string;
  title: string;
  html: string;
}

export interface StructuredArticleContent {
  introHtml: string;
  sections: StructuredArticleSection[];
}

const escapeHtml = (value: string) =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const stripHtml = (value: string) => value.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();

const textNodeToHtml = (value: string) => {
  const text = value.replace(/\s+/g, ' ').trim();
  return text ? `<p>${escapeHtml(text)}</p>` : '';
};

const blockToHtml = (block: string) => {
  const lines = block
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);

  if (!lines.length) {
    return '';
  }

  if (lines.every((line) => /^[-*]\s+/.test(line))) {
    return `<ul>${lines
      .map((line) => `<li>${escapeHtml(line.replace(/^[-*]\s+/, ''))}</li>`)
      .join('')}</ul>`;
  }

  if (lines.every((line) => /^\d+[.)]\s+/.test(line))) {
    return `<ol>${lines
      .map((line) => `<li>${escapeHtml(line.replace(/^\d+[.)]\s+/, ''))}</li>`)
      .join('')}</ol>`;
  }

  return lines.map((line) => `<p>${escapeHtml(line)}</p>`).join('');
};

const withAutoTitle = (title: string, index: number) => title || `Раздел ${index + 1}`;

const parseHtmlContent = (content: string): StructuredArticleContent => {
  const parser = new DOMParser();
  const document = parser.parseFromString(content, 'text/html');
  const introFragments: string[] = [];
  const sections: StructuredArticleSection[] = [];
  let currentTitle = '';
  let currentFragments: string[] = [];

  const pushSection = () => {
    const html = currentFragments.join('').trim();
    if (!html) {
      currentFragments = [];
      return;
    }

    sections.push({
      id: `section-${sections.length + 1}`,
      title: withAutoTitle(currentTitle, sections.length),
      html,
    });
    currentTitle = '';
    currentFragments = [];
  };

  Array.from(document.body.childNodes).forEach((node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      const html = textNodeToHtml(node.textContent || '');
      if (!html) {
        return;
      }

      if (!currentTitle && sections.length === 0) {
        introFragments.push(html);
      } else {
        currentFragments.push(html);
      }
      return;
    }

    if (!(node instanceof HTMLElement)) {
      return;
    }

    const tagName = node.tagName.toUpperCase();

    if (tagName === 'H1') {
      return;
    }

    if (tagName === 'H2' || tagName === 'H3') {
      pushSection();
      currentTitle = stripHtml(node.textContent || '');
      return;
    }

    const html = node.outerHTML.trim();
    if (!html) {
      return;
    }

    if (!currentTitle && sections.length === 0) {
      introFragments.push(html);
    } else {
      currentFragments.push(html);
    }
  });

  pushSection();

  let introHtml = introFragments.join('').trim();

  if (!sections.length && introHtml) {
    sections.push({
      id: 'section-1',
      title: 'Материал',
      html: introHtml,
    });
    introHtml = '';
  }

  return { introHtml, sections };
};

const parsePlainTextContent = (content: string): StructuredArticleContent => {
  const blocks = content
    .split(/\n{2,}/)
    .map((block) => block.trim())
    .filter(Boolean);

  const introFragments: string[] = [];
  const sections: StructuredArticleSection[] = [];
  let currentTitle = '';
  let currentBlocks: string[] = [];

  const pushSection = () => {
    const html = currentBlocks.map(blockToHtml).join('').trim();
    if (!html) {
      currentBlocks = [];
      return;
    }

    sections.push({
      id: `section-${sections.length + 1}`,
      title: withAutoTitle(currentTitle, sections.length),
      html,
    });
    currentTitle = '';
    currentBlocks = [];
  };

  blocks.forEach((block, index) => {
    if (/^#{1,3}\s+/.test(block)) {
      pushSection();
      currentTitle = block.replace(/^#{1,3}\s+/, '').trim();
      return;
    }

    if (!currentTitle && sections.length === 0 && index === 0) {
      introFragments.push(blockToHtml(block));
      return;
    }

    currentBlocks.push(block);
  });

  pushSection();

  let introHtml = introFragments.join('').trim();

  if (!sections.length && introHtml) {
    sections.push({
      id: 'section-1',
      title: 'Материал',
      html: introHtml,
    });
    introHtml = '';
  }

  return { introHtml, sections };
};

export const structureArticleContent = (content?: string): StructuredArticleContent => {
  const value = content?.trim() || '';

  if (!value) {
    return { introHtml: '', sections: [] };
  }

  return value.startsWith('<') ? parseHtmlContent(value) : parsePlainTextContent(value);
};

export const normalizeArticleText = (value?: string) =>
  stripHtml(value || '').toLowerCase().replace(/\s+/g, ' ').trim();
